(function (root, factory) {
  var api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.SubmissionJigQuiz = api;
  }
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  var VALID = {
    water: ['fresh', 'salt', 'both'],
    depth: ['shallow', 'mid', 'deep'],
    experience: ['new', 'casual', 'expert']
  };

  function clean(value) {
    return String(value || '').trim();
  }

  function allowed(group, value) {
    return VALID[group].indexOf(clean(value)) !== -1 ? clean(value) : '';
  }

  function normalizeAnswers(answers) {
    answers = answers || {};

    return {
      water: allowed('water', answers.water),
      depth: allowed('depth', answers.depth),
      experience: allowed('experience', answers.experience)
    };
  }

  function answersComplete(answers) {
    var normalized = normalizeAnswers(answers);
    return Boolean(normalized.water && normalized.depth && normalized.experience);
  }

  // Expert results are matched by depth alone (one dialed-in jig per depth
  // tier, regardless of water). New and Casual results are matched by
  // water + depth + experience — bundle picks differ by water type, and
  // New vs. Casual pick different bundles at the same water/depth, so
  // experience has to stay in the key or the two tiers would collide.
  function recommendationKey(answers) {
    var normalized = normalizeAnswers(answers);
    if (!normalized.depth || !normalized.experience) return '';

    if (normalized.experience === 'expert') {
      return normalized.depth;
    }

    if (!normalized.water) return '';
    return normalized.water + '-' + normalized.depth + '-' + normalized.experience;
  }

  function validEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean(value));
  }

  function absoluteUrl(value, shopUrl) {
    var url = clean(value);
    if (!url) return '';

    try {
      return new URL(url, clean(shopUrl) || undefined).toString();
    } catch (error) {
      return url;
    }
  }

  function labelsFor(answers, labels) {
    var normalized = normalizeAnswers(answers);
    labels = labels || {};

    return {
      water: clean(labels.water && labels.water[normalized.water]) || normalized.water,
      depth: clean(labels.depth && labels.depth[normalized.depth]) || normalized.depth,
      experience: clean(labels.experience && labels.experience[normalized.experience]) || normalized.experience
    };
  }

  function buildProfileProperties(input) {
    input = input || {};
    var answers = normalizeAnswers(input.answers);
    var labels = labelsFor(answers, input.labels);
    var recommendation = input.recommendation || {};

    return {
      'Water Type': labels.water,
      'Fishing Depth': labels.depth,
      'Slow Pitch Experience': labels.experience,
      'Recommended Jig': clean(recommendation.productTitle || recommendation.heading),
      'Recommended Product URL': absoluteUrl(recommendation.productUrl, input.shopUrl),
      'Recommended Jig Weight': clean(recommendation.weight),
      'Submission Water Key': answers.water,
      'Submission Depth Key': answers.depth,
      'Submission Experience Key': answers.experience,
      'Submission Jig Quiz Result Key': recommendationKey(answers),
      'Submission Last Quiz': 'Find Your Jig',
      'Submission Quiz Completed At': clean(input.timestamp)
    };
  }

  function buildEventProperties(input) {
    input = input || {};
    var answers = normalizeAnswers(input.answers);
    var labels = labelsFor(answers, input.labels);
    var recommendation = input.recommendation || {};
    var productTitle = clean(recommendation.productTitle || recommendation.heading);

    return {
      'Quiz Name': 'Find Your Jig',
      'Quiz ID': 'submission-jig-finder',
      'Answers': {
        'Water Type': labels.water,
        'Fishing Depth': labels.depth,
        'Slow Pitch Experience': labels.experience
      },
      'Result Segment': recommendationKey(answers),
      'Recommended Products': productTitle ? [productTitle] : [],
      'Recommended Jig': productTitle,
      'Recommended Product URL': absoluteUrl(recommendation.productUrl, input.shopUrl),
      'Recommended Jig Weight': clean(recommendation.weight),
      'Completed At': clean(input.timestamp)
    };
  }

  function buildSubscriptionPayload(input) {
    input = input || {};
    var timestamp = clean(input.timestamp) || new Date().toISOString();
    var data = {
      type: 'subscription',
      attributes: {
        profile: {
          data: {
            type: 'profile',
            attributes: {
              email: clean(input.email),
              properties: buildProfileProperties(input),
              subscriptions: {
                email: {
                  marketing: {
                    consent: 'SUBSCRIBED',
                    consented_at: timestamp
                  }
                }
              }
            }
          }
        },
        custom_source: clean(input.source) || 'Shopify Jig Finder'
      }
    };

    if (clean(input.listId)) {
      data.relationships = {
        list: {
          data: {
            type: 'list',
            id: clean(input.listId)
          }
        }
      };
    }

    return { data: data };
  }

  function buildEventPayload(input) {
    input = input || {};
    var timestamp = clean(input.timestamp) || new Date().toISOString();

    return {
      data: {
        type: 'event',
        attributes: {
          time: timestamp,
          unique_id: clean(input.uniqueId),
          properties: buildEventProperties(input),
          metric: {
            data: {
              type: 'metric',
              attributes: {
                name: clean(input.eventName) || 'Jig Quiz Completed'
              }
            }
          },
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: clean(input.email),
                properties: buildProfileProperties(input)
              }
            }
          }
        }
      }
    };
  }

  function customerTags(answers) {
    var normalized = normalizeAnswers(answers);
    var tags = ['prospect', 'jig-quiz'];

    Object.keys(normalized).forEach(function (key) {
      if (normalized[key]) tags.push('jig-quiz-' + normalized[key]);
    });

    return tags.join(',');
  }

  function uniqueId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }

    return 'jig-quiz-' + Date.now() + '-' + Math.random().toString(16).slice(2);
  }

  function postJson(url, body, revision, timeout) {
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, timeout || 6000) : null;

    return fetch(url, {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        revision: revision
      },
      body: JSON.stringify(body),
      signal: controller ? controller.signal : undefined
    }).then(function (response) {
      if (timer) clearTimeout(timer);
      if (!response.ok) {
        throw new Error('Klaviyo request returned ' + response.status);
      }
      return response;
    }).catch(function (error) {
      if (timer) clearTimeout(timer);
      throw error;
    });
  }

  function initSection(section) {
    if (!section || section.dataset.jigQuizReady === 'true') return;
    section.dataset.jigQuizReady = 'true';
    section.classList.add('is-enhanced');

    var form = section.querySelector('[data-jig-quiz-form], .jig-quiz__form');
    var steps = Array.prototype.slice.call(section.querySelectorAll('[data-quiz-step]'));
    var result = section.querySelector('[data-quiz-result]');
    var cards = Array.prototype.slice.call(section.querySelectorAll('[data-result-key]'));
    var progressFill = section.querySelector('[data-progress-fill]');
    var progressText = section.querySelector('[data-progress-text]');
    var status = section.querySelector('[data-quiz-status]');
    var tagsInput = section.querySelector('[data-quiz-tags]');
    var retryButton = section.querySelector('[data-quiz-retry]');
    var submitButton = section.querySelector('[data-quiz-submit]');
    var current = 0;
    var pendingSubmission = null;

    if (!form || !steps.length || !result) return;

    function setStatus(message, state) {
      if (!status) return;
      status.textContent = clean(message);
      status.dataset.state = state || '';
    }

    function readAnswers() {
      var answers = {};
      Object.keys(VALID).forEach(function (key) {
        var checked = form.querySelector('[data-answer="' + key + '"]:checked');
        answers[key] = checked ? checked.value : '';
      });
      return normalizeAnswers(answers);
    }

    function labels() {
      return {
        water: {
          fresh: section.dataset.waterFresh,
          salt: section.dataset.waterSalt,
          both: section.dataset.waterBoth
        },
        depth: {
          shallow: section.dataset.depthShallow,
          mid: section.dataset.depthMid,
          deep: section.dataset.depthDeep
        },
        experience: {
          new: section.dataset.expNew,
          casual: section.dataset.expCasual,
          expert: section.dataset.expExpert
        }
      };
    }

    function showStep(index, focusHeading) {
      current = Math.max(0, Math.min(index, steps.length - 1));
      steps.forEach(function (step, stepIndex) {
        step.hidden = stepIndex !== current;
        step.setAttribute('aria-hidden', stepIndex === current ? 'false' : 'true');
      });

      var completed = ((current + 1) / steps.length) * 100;
      if (progressFill) progressFill.style.width = completed + '%';
      if (progressText) progressText.textContent = (current + 1) + ' / ' + steps.length;
      setStatus('', '');

      if (focusHeading) {
        var heading = steps[current].querySelector('[data-step-heading]');
        if (heading) heading.focus({ preventScroll: true });
      }
    }

    function currentStepIsValid() {
      var answerName = steps[current].dataset.quizStep;
      if (answerName === 'email') return true;
      return Boolean(form.querySelector('[data-answer="' + answerName + '"]:checked'));
    }

    function selectResult(answers) {
      var key = recommendationKey(answers);
      var selected = null;

      cards.forEach(function (card) {
        var match = card.dataset.resultKey === key && !selected;
        card.hidden = !match;
        card.setAttribute('aria-hidden', match ? 'false' : 'true');
        if (match) selected = card;
      });

      if (!selected) return null;

      var waterNote = section.dataset[answers.water + 'Note'] || '';
      var waterOutput = selected.querySelector('[data-water-note]');
      if (waterOutput) waterOutput.textContent = waterNote;

      return {
        heading: selected.dataset.heading,
        productTitle: selected.dataset.productTitle,
        productUrl: selected.dataset.productUrl,
        weight: selected.dataset.weight
      };
    }

    function makeSubmission(email, answers, recommendation) {
      return {
        email: clean(email).toLowerCase(),
        answers: normalizeAnswers(answers),
        labels: labels(),
        recommendation: recommendation,
        source: 'Shopify Jig Finder',
        shopUrl: section.dataset.shopUrl,
        listId: section.dataset.klaviyoListId,
        eventName: section.dataset.eventName,
        timestamp: new Date().toISOString(),
        uniqueId: uniqueId()
      };
    }

    function sendSubmission(submission) {
      var siteId = clean(section.dataset.klaviyoSiteId);
      var revision = clean(section.dataset.klaviyoRevision) || '2026-07-15';
      if (!siteId) return Promise.reject(new Error('Klaviyo public site ID is missing'));

      var query = '?company_id=' + encodeURIComponent(siteId);
      return postJson(
        'https://a.klaviyo.com/client/subscriptions/' + query,
        buildSubscriptionPayload(submission),
        revision
      ).then(function () {
        return postJson(
          'https://a.klaviyo.com/client/events/' + query,
          buildEventPayload(submission),
          revision
        );
      });
    }

    function finishSubmission(submission) {
      pendingSubmission = submission;
      try {
        sessionStorage.setItem('submissionJigQuizPending', JSON.stringify(submission));
      } catch (error) {
        // Storage is a retry convenience; the request can continue without it.
      }

      if (submitButton) submitButton.disabled = true;
      if (retryButton) retryButton.hidden = true;
      setStatus(section.dataset.sendingCopy, 'sending');

      return sendSubmission(submission).then(function () {
        var successCopy = section.dataset.emailFlowLive === 'true'
          ? section.dataset.emailSuccessCopy
          : section.dataset.successCopy;
        setStatus(successCopy, 'success');
        pendingSubmission = null;
        try { sessionStorage.removeItem('submissionJigQuizPending'); } catch (error) {}
      }).catch(function () {
        setStatus(section.dataset.failureCopy, 'error');
        if (retryButton) retryButton.hidden = false;
      }).finally(function () {
        if (submitButton) submitButton.disabled = false;
      });
    }

    form.addEventListener('change', function () {
      var answers = readAnswers();
      if (tagsInput) tagsInput.value = customerTags(answers);
      setStatus('', '');
    });

    section.addEventListener('click', function (event) {
      var next = event.target.closest('[data-quiz-next]');
      var back = event.target.closest('[data-quiz-back]');
      var restart = event.target.closest('[data-quiz-restart]');

      if (next) {
        if (!currentStepIsValid()) {
          setStatus(section.dataset.answerRequired, 'error');
          return;
        }
        showStep(current + 1, true);
      }

      if (back) showStep(current - 1, true);

      if (restart) {
        form.reset();
        cards.forEach(function (card) { card.hidden = true; });
        result.hidden = true;
        form.hidden = false;
        pendingSubmission = null;
        if (retryButton) retryButton.hidden = true;
        if (tagsInput) tagsInput.value = 'prospect,jig-quiz';
        showStep(0, true);
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var answers = readAnswers();
      var emailInput = form.querySelector('[data-quiz-email]');
      var consentInput = form.querySelector('[data-quiz-consent]');
      var email = emailInput ? emailInput.value : '';

      if (!answersComplete(answers)) {
        setStatus(section.dataset.answerRequired, 'error');
        showStep(0, true);
        return;
      }
      if (!validEmail(email)) {
        setStatus(section.dataset.emailRequired, 'error');
        if (emailInput) emailInput.focus();
        return;
      }
      if (!consentInput || !consentInput.checked) {
        setStatus(section.dataset.consentRequired, 'error');
        if (consentInput) consentInput.focus();
        return;
      }

      var recommendation = selectResult(answers);
      if (!recommendation) {
        setStatus(section.dataset.missingResult, 'error');
        return;
      }

      form.hidden = true;
      result.hidden = false;
      result.scrollIntoView({ behavior: 'smooth', block: 'start' });
      finishSubmission(makeSubmission(email, answers, recommendation));
    });

    if (retryButton) {
      retryButton.addEventListener('click', function () {
        if (pendingSubmission) finishSubmission(pendingSubmission);
      });
    }

    section.addEventListener('shopify:block:select', function (event) {
      var card = event.target.closest && event.target.closest('[data-result-key]');
      if (!card) return;

      form.hidden = true;
      result.hidden = false;
      cards.forEach(function (resultCard) {
        var selected = resultCard === card;
        resultCard.hidden = !selected;
        resultCard.setAttribute('aria-hidden', selected ? 'false' : 'true');
      });
    });

    showStep(0, false);
  }

  function initAll(container) {
    var scope = container && container.querySelectorAll ? container : document;
    var sections = scope.matches && scope.matches('[data-jig-quiz-section]')
      ? [scope]
      : Array.prototype.slice.call(scope.querySelectorAll('[data-jig-quiz-section]'));
    sections.forEach(initSection);
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { initAll(document); });
    } else {
      initAll(document);
    }

    document.addEventListener('shopify:section:load', function (event) {
      initAll(event.target);
    });
  }

  return {
    normalizeAnswers: normalizeAnswers,
    answersComplete: answersComplete,
    recommendationKey: recommendationKey,
    validEmail: validEmail,
    buildProfileProperties: buildProfileProperties,
    buildEventProperties: buildEventProperties,
    buildSubscriptionPayload: buildSubscriptionPayload,
    buildEventPayload: buildEventPayload,
    customerTags: customerTags
  };
});
